import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, XMarkIcon, PhotoIcon, DocumentTextIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { processFileForSignal, ingestSignal } from '../../../utils/api';
import { ChannelType } from '../../../types/signal';
import { ThreadMessage, normalizeThreadMessage } from '../../../types/threadMessage';
import { APIErrorException } from '../../../services/types/errors';
import { toast } from 'sonner';
import DatasetSelector from '../shared/DatasetSelector';
import { useQueryClient } from '@tanstack/react-query';
import { datasetKeys } from '../../../hooks/useDatasets';

interface SmartSignalProcessorProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (response: any) => void;
  onBack?: () => void;
}

interface ExtractedSignalData {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_id?: string;
  verbatim: string;
  channel: ChannelType;
  product_policy?: string;
  journey_stage?: string;
  source?: string;
  thread_messages?: ThreadMessage[];
}

const SmartSignalProcessor: React.FC<SmartSignalProcessorProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onBack
}) => {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedSignalData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [datasetId, setDatasetId] = useState<string | null>(null);

  const processFile = useCallback(async (file: File) => {
    setIsProcessing(true);
    
    try {
      console.log('Processing file:', file.name, file.type);
      const response = await processFileForSignal(file);
      
      if (response && response.success && response.extracted_data) {
        // Map the extracted data to our format
        const data = response.extracted_data;
        
        // Clean up channel type
        let cleanChannel = (data.channel || 'email').toLowerCase().trim();
        const channelMapping: { [key: string]: ChannelType } = {
          'document': 'email',
          'image': 'email',
          'screenshot': 'chat',
          'email': 'email',
          'chat': 'chat',
          'call': 'call_center',
          'phone': 'call_center',
          'social': 'social_media',
          'review': 'review',
          'survey': 'survey',
          'csv': 'csv'
        };
        
        if (channelMapping[cleanChannel]) {
          cleanChannel = channelMapping[cleanChannel];
        } else if (!['call_center', 'social_media', 'email', 'chat', 'survey', 'review', 'csv'].includes(cleanChannel)) {
          cleanChannel = 'email';
        }
        
        // Normalize thread messages to ensure consistent format
        const normalizedMessages: ThreadMessage[] = (data.thread_messages || []).map((msg: any) => 
          normalizeThreadMessage(msg, data.customer_name || 'Customer')
        );
        
        const extractedSignal: ExtractedSignalData = {
          customer_name: data.customer_name || '',
          customer_email: data.customer_email || '',
          customer_phone: data.customer_phone || '',
          customer_id: data.customer_id || '',
          verbatim: data.verbatim || '',
          channel: cleanChannel as ChannelType,
          product_policy: data.product_policy || data.customer_product_policy || '',
          journey_stage: data.journey_stage || '',
          source: data.source || file.name,
          thread_messages: normalizedMessages
        };
        
        setExtractedData(extractedSignal);
        setShowPreview(true);
      } else {
        toast.error(response.error || 'Failed to extract signal data from file');
      }
    } catch (err) {
      console.error('Error processing file:', err);
      // Extract error message from APIErrorException or fallback to generic message
      if (err instanceof APIErrorException) {
        toast.error(err.error.message || 'Failed to process file');
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Failed to process file');
      }
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validDocTypes = ['application/pdf', 'text/plain', 'application/msword', 
                           'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    const isImage = validImageTypes.includes(file.type) || file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isDocument = validDocTypes.includes(file.type) || file.name.match(/\.(pdf|txt|doc|docx)$/i);
    
    if (!isImage && !isDocument) {
      toast.error('Please select a valid image or document file');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setExtractedData(null);
    
    // Process the file immediately
    await processFile(file);
  }, [processFile]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleUpdateExtractedData = useCallback((field: keyof ExtractedSignalData, value: any) => {
    if (extractedData) {
      setExtractedData({
        ...extractedData,
        [field]: value
      });
    }
  }, [extractedData]);

  const handleSubmit = useCallback(async () => {
    if (!extractedData) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare the signal data for the API
      const signalData: any = {
        raw_text: extractedData.verbatim,
        channel_type: extractedData.channel,
        customer_name: extractedData.customer_name || undefined,
        customer_id: extractedData.customer_id || undefined,
        customer_email: extractedData.customer_email || undefined,
        customer_phone: extractedData.customer_phone || undefined,
        occurred_at: new Date().toISOString(),
        journey_stage: extractedData.journey_stage || undefined,
        product_category: extractedData.product_policy || undefined,
        dataset_id: datasetId || undefined,
        channel_metadata: {
          source_type: 'smart_extract',
          journey_stage: extractedData.journey_stage || undefined,
          created_via: 'signal_creation_hub',
          form_version: '3.0',
          original_file: selectedFile?.name
        },
        thread_messages: extractedData.thread_messages || [],
        source_id: extractedData.source || undefined,
        source_type: 'smart_extract'
      };

      const response = await ingestSignal(signalData);

      // Invalidate datasets query to refresh the feedback set table
      await queryClient.invalidateQueries({ queryKey: datasetKeys.all });

      onSubmit(response);
      
    } catch (err) {
      console.error('Error creating Record:', err);
      if (err instanceof APIErrorException) {
        toast.error(err.error.message || 'Failed to create Record');
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Failed to create Record');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [extractedData, selectedFile, datasetId, queryClient, onSubmit]);

  // Early return must be after all hooks
  if (!isOpen) return null;

  const channelOptions: { value: ChannelType; label: string }[] = [
    { value: 'chat', label: 'Chat' },
    { value: 'call_center', label: 'Call Center' },
    { value: 'email', label: 'Email' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'survey', label: 'Survey' },
    { value: 'review', label: 'Review' },
    { value: 'csv', label: 'CSV Import' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      style={{ fontFamily: 'Open Sans, sans-serif' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isProcessing || isSubmitting}
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Smart Record Extraction</h2>
            <p className="text-sm text-gray-600 mt-1">
              Extract Record data from images or documents using AI
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          disabled={isProcessing || isSubmitting}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!showPreview ? (
          <>
            {/* File Upload Area */}
            <div
              className={`
                relative border-2 border-dashed rounded-xl p-12 text-center transition-all
                ${isDragging ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'}
                ${selectedFile ? 'bg-green-50 border-green-300' : ''}
                ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => !isProcessing && document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*,.pdf,.txt,.doc,.docx"
                onChange={handleFileInputChange}
                className="hidden"
                disabled={isProcessing}
              />
              
              {isProcessing ? (
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                  <div>
                    <p className="text-lg font-medium text-gray-900">Processing {selectedFile?.name}...</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Extracting Record data using AI
                    </p>
                  </div>
                </div>
              ) : selectedFile ? (
                <div className="space-y-4">
                  <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto" />
                  <div>
                    <p className="text-lg font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-600">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setExtractedData(null);
                    }}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-center space-x-4">
                    <PhotoIcon className="w-12 h-12 text-purple-400" />
                    <DocumentTextIcon className="w-12 h-12 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      Drop an image or document here
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      or click to browse
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Supports: Images (JPG, PNG), PDFs, Documents (DOC, DOCX, TXT)
                  </p>
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="mt-8 bg-purple-50 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-purple-900 mb-3">Tips for best results:</h3>
              <ul className="space-y-2 text-sm text-purple-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Upload clear screenshots of customer communications</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Include the full conversation thread when possible</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Ensure text is readable and not cut off</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>PDFs and documents work best with structured content</span>
                </li>
              </ul>
            </div>
          </>
        ) : extractedData && (
          /* Preview and Edit Extracted Data */
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                <p className="text-green-800 font-medium">
                  Successfully extracted Record data from {selectedFile?.name}
                </p>
              </div>
            </div>

            {/* Dataset Selection */}
            <DatasetSelector
              value={datasetId}
              onChange={setDatasetId}
              autoSelectDefault={false}
            />

            {/* Editable Form */}
            <div className="space-y-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name
                    </label>
                    <input
                      type="text"
                      value={extractedData.customer_name}
                      onChange={(e) => handleUpdateExtractedData('customer_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Enter customer name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={extractedData.customer_email}
                      onChange={(e) => handleUpdateExtractedData('customer_email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      placeholder="customer@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* Record Content */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Record Content</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Verbatim
                  </label>
                  <textarea
                    value={extractedData.verbatim}
                    onChange={(e) => handleUpdateExtractedData('verbatim', e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Customer's message or feedback..."
                  />
                </div>
              </div>

              {/* Channel and Context */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Context</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Channel
                    </label>
                    <select
                      value={extractedData.channel}
                      onChange={(e) => handleUpdateExtractedData('channel', e.target.value as ChannelType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                    >
                      {channelOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source
                    </label>
                    <input
                      type="text"
                      value={extractedData.source}
                      onChange={(e) => handleUpdateExtractedData('source', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Source reference"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {showPreview && extractedData && (
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                setShowPreview(false);
                setExtractedData(null);
                setSelectedFile(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Start Over
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !extractedData.verbatim || !datasetId}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Record...
                </>
              ) : (
                'Create Record'
              )}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SmartSignalProcessor;